<script lang="ts">
	//Hooks
	import { createEventDispatcher, onMount } from 'svelte';
	//Formatting
	import { format } from 'svelte-i18n';
	//Design system components
	import {
		Alert,
		Button,
		Container,
		Input,
		Modal,
		SelectCard,
		Text,
		Toggle,
		Wizard,
		WizardContainer,
		WizardContent,
		WizardItem,
		CardGrid,
		type SelectOptionPropsType,
	} from '@Shelly/shelly-design-system';
	//Components
	import DoubleTimePicker from '../../../TimePicker/DoubleTimePicker.svelte';
	import WebhookConditions from './Conditions/WebhookConditions.svelte';
	//Helpers
	import { getWebHookEvents } from 'src/helpers/actions/webhooks/core/webhook_helpers';
	import {
		getWizardItems,
		DEVICES_WITHOUT_ACTIVE_TIME_STEP,
	} from 'src/helpers/actions/webhooks/view/webhook_view_helpers';
	import { deepCopy, isValidURL } from 'src/helpers/utilities';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import WebHooksActions from './Actions/WebHooksActions.svelte';
	import ExitConfirmationModal from 'src/components/ExitConfirmationModal.svelte';
	//Services
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	//Types
	import type { Device } from 'src/types/Device';
	import type {
		WebHooksParams,
		WebHooksRequest,
		WebHookSupportedList,
	} from 'src/types/requests/settings/webhooks';
	// Stores
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let showWizard: boolean,
		peripherals: any[] = null,
		actionProps: WebHooksParams = null,
		supportedActionsList: WebHookSupportedList,
		device: Device;

	let isInputDirty = false;
	let disabled = false;

	$: isLastStepWizard = activeStep.activeIndex == wizardItems.length - 1;

	let disabledConditions = [];
	const hideTimePicker = DEVICES_WITHOUT_ACTIVE_TIME_STEP.includes(device.type);

	$: {
		const isNameEmpty = !actionProps.name || actionProps.name.trim() === '';
		const hasValidConditions = eventsIndex !== -1;
		const hasValidUrls = actionProps.urls.length > 0 && actionProps.urls[0] !== '';

		disabledConditions = chooseComponent
			? [false, isNameEmpty, isNameEmpty, !hasValidConditions || isNameEmpty, !hasValidUrls]
			: hideTimePicker
				? [isNameEmpty, !hasValidConditions || isNameEmpty, !hasValidUrls]
				: [isNameEmpty, isNameEmpty, !hasValidConditions || isNameEmpty, !hasValidUrls];
		disabled = disabledConditions[activeStep.activeIndex];
	}

	const closeModalTimeout = 300;
	const dispatch = createEventDispatcher(),
		components = actionProps.components,
		wizardItems = getWizardItems(
			peripherals ? 'peripheral' : components ? 'component' : null,
			hideTimePicker,
		),
		title = actionProps?.id
			? $format('edit_action', { default: 'Edit action' })
			: $format('create_action', { default: 'Create action' }),
		errors = {
			name: {
				show: false,
				label: $format('enter_web_name', {
					default: 'Please set an action name!',
				}),
			},
			condition: {
				show: false,
				label: $format('field_cannot_be_empty', {
					default: 'This field cannot be empty',
				}),
			},
			urls: {
				show: false,
				required: $format('field_url_cannot_be_empty', {
					default: 'Invalid argument URLs: At least one URL is required!',
				}),
				invalid: $format('enter_valid_url', {
					default: 'Invalid URLs: Please enter a valid URL!',
				}),
			},
		},
		onPeripheralChange = (index: number) => {
			peripheralsIndex = index;
			const peripheral = peripherals[index];
			actionProps.cid = peripheral.sensor.extid;
			actionProps.inputType = peripheral.sensor.inputType;
			actionProps.type = peripheral.sensor.type;
			actionProps.unit = peripheral.sensor.unit;
			actionProps.icon = peripheral.sensor.icon;
			updateEventsOptions();
		},
		onComponentsChange = (index: number) => {
			componentIndex = index;
			actionProps = { ...actionProps, ...components[index] };
			updateEventsOptions();
		},
		updateEventsOptions = () => {
			const events = getWebHookEvents(actionProps, supportedActionsList, device);
			eventsOptions = events.options;
			eventsIndex = eventsOptions.length == 1 ? 0 : events.index;
		},
		changeEvent = (e: CustomEvent) => {
			eventsIndex = e.detail.value;
			actionProps.event = e.detail.event;
		},
		save = async () => {
			const eventProps = eventsOptions[eventsIndex],
				data: WebHooksParams = {
					cid: actionProps.cid,
					id: actionProps.id,
					enable: actionProps.enable !== false,
					name: actionProps.name,
					event: eventProps.event,
					condition: '',
					urls: actionProps.urls,
				};
			if (/^(SPEM-002CEBEU50)$/.test(device.type)) {
				// ProEM 50
				// One output with 2 meter channels
				data.cid = peripherals ? 100 : data.event.startsWith('switch.') ? 0 : device.channel;
			}
			if (!hideTimePicker) {
				// Sleeping devices do not accept `set_active_time`
				data.active_between = actionProps.set_active_time ? actionProps.active_between : null;
			}
			if (actionProps.repeat_period != null) {
				data.repeat_period = actionProps.repeat_period;
			}
			if (!data.name) {
				errors.name.show = true;
				activeStep = wizardItems[chooseComponent ? 1 : 0];
				return;
			} else if (eventProps?.conditions) {
				let condition = [];
				const conditions = eventProps.conditions;
				for (let i = 0; i < conditions.length; ++i) {
					if (conditions[i].conditionsIndex != -1) {
						if (conditions[i].conditionValue === '') {
							errors.condition.show = true;
							activeStep = wizardItems[chooseComponent ? 3 : hideTimePicker ? 1 : 2];
							return;
						}
						let event = conditions[i].conditionEvent;
						const conditionEvent =
							conditions[i].conditions?.[conditions[i]?.conditionsIndex]?.conditionEvent;
						if (conditionEvent != null) event = conditionEvent;
						condition.push(
							`${event} ${
								conditions[i].conditions[conditions[i].conditionsIndex].value
							} ${conditions[i].conditionValue}`.trim(),
						);
					}
				}
				if (condition.length) {
					data.condition = condition.join(' && ');
				}
			}
			for (let i = 0; i < data.urls.length; ++i) {
				if (!isValidURL(data.urls[i], true)) {
					errors.urls.show = true;
					return;
				}
			}
			waitForResponse = true;
			const res: WebHooksRequest = await gen2Request(
				device,
				`webhook.${data.id ? 'update' : 'create'}`,
				data,
			);
			if (res?.isok) {
				// // Give some time before the next request to ensure the webhooks list is updated
				showWizard = false;
				setTimeout(() => {
					dispatch('save', data);
				}, closeModalTimeout);
			}
			waitForResponse = false;
			onSettingsResponse(res, device, {
				success: $format('action_saved_scc_toast', {
					default: 'Action successfully saved!',
				}),
			});
		},
		peripheralsOptions: SelectOptionPropsType[] = [],
		componentsOptions: SelectOptionPropsType[] = [];

	let chooseComponent = peripherals != null || components != null,
		activeStep = wizardItems[0],
		waitForResponse = false,
		peripheralsIndex = 0,
		componentIndex = 0,
		eventsIndex = -1,
		eventsOptions: any[];

	actionProps.set_active_time =
		actionProps.active_between != null && actionProps.active_between?.length > 0;
	if (!actionProps.active_between || (actionProps.active_between.length === 0 && !hideTimePicker)) {
		actionProps.active_between = ['00:00', '00:00'];
	}
	if (!actionProps.urls) {
		actionProps.urls = [];
	}
	if (peripherals) {
		peripherals.forEach((el, i) => {
			peripheralsOptions.push({
				value: i,
				title: el.sensor.name || el.sensor.alias,
			});
			if (actionProps.id != null && el.sensor.component == actionProps.component) {
				peripheralsIndex = i;
			}
		});
	} else if (components) {
		components.forEach((el, i) => {
			componentsOptions.push({
				value: i,
				title: el.title,
			});
			if (
				actionProps.id != null &&
				el.component == actionProps.event.split('.')[0] + ':' + actionProps.cid
			) {
				onComponentsChange(i);
			} else if (actionProps.id == null && i == 0) {
				onComponentsChange(0);
			}
		});
	}
	updateEventsOptions();

	let showModal = false;
	onMount(() => (showModal = true));

	let showExitConfirmationModal = false;
	let exitConfirmationModalState = false;
	let clonedActionObject = deepCopy(actionProps);

	const keyPress = (e) => {
		e.stopImmediatePropagation();
	};
	$: if (showModal) {
		window.addEventListener('keydown', keyPress);
	} else {
		window.removeEventListener('keydown', keyPress);
	}
</script>

<Modal
	{title}
	{showModal}
	on:close={() => {
		if (JSON.stringify(actionProps) !== JSON.stringify(clonedActionObject)) {
			showExitConfirmationModal = true;
			exitConfirmationModalState = false;
		} else {
			showModal = false;
			setTimeout(() => (showWizard = false), 300);
		}
	}}
	removeScrollPadding
>
	<Wizard
		slot="toolbar"
		activeStepNumber={activeStep.activeIndex}
		stepsTotal={wizardItems.length}
		activeStepTitle={activeStep.title}
	>
		{#each wizardItems as item, i (i)}
			<WizardItem
				on:click={() => {
					activeStep = wizardItems[i];
				}}
				active={activeStep.activeIndex === i}
				title={item.title}
				disabled={disabledConditions[i - 1]}
				indicatorColor={$showNewLayout ? 'color-2' : undefined}
			/>
		{/each}
	</Wizard>

	<div class:flex-overflow-hidden={false} slot="content">
		<WizardContainer activeStepIndex={activeStep.activeIndex} adaptiveHeight={true}>
			{#each wizardItems as item, i (i)}
				<WizardContent active={activeStep.activeIndex === i} class={'pl-0 pr-0 pb-s'}>
					{@const isTimePickerStep =
						(chooseComponent && i == 2) ||
						(!hideTimePicker && i == 1 && !(chooseComponent && i == 1))}
					{#if isTimePickerStep}
						<Container padding="scrollPaddings">
							<Alert
								class="mb-m"
								type="info"
								heading={$format('alert_message_always_active_action', {
									default:
										'The action will be always active unless you want to set custom active time to best suit your preferences.',
								})}
							/>
						</Container>
					{:else if (chooseComponent && i == 3) || i == (hideTimePicker ? 1 : 2)}
						<Container padding="scrollPaddings">
							<Alert
								class="mb-m"
								type="info"
								heading={$format('alert_message_threshold_trigger_event', {
									default:
										'The event would trigger when the configured threshold is reached.',
								})}
							/>
						</Container>
					{:else if (chooseComponent && i == 4) || i == (hideTimePicker ? 2 : 3)}
						<Container padding="scrollPaddings">
							<Alert
								class="mb-m"
								heading={actionProps.urls.length >= 5
									? $format('alert_warning_heading_actions_limit_reached', {
											default: 'Actions limit reached',
										})
									: $format('alert_info_heading_add_5_actions', {
											default: 'You can add up to 5 actions.',
										})}
								isHeadingBold={actionProps.urls.length >= 5}
								message={actionProps.urls.length >= 5
									? $format('alert_warning_message_actions_limit_description', {
											default:
												'You have reached the limit of 5 actions. If you want to add a new one, please delete one of the actions you have already added.',
										})
									: ''}
								type={actionProps.urls.length >= 5 ? 'warning' : 'info'}
							/>
						</Container>
					{/if}

					<Container noScroll padding="scrollPaddings">
						{#if activeStep.activeIndex != wizardItems.length - 1 && !isTimePickerStep && item.subtitle}
							<Text element="h2" class="mb-s">
								{item.subtitle}
							</Text>
						{/if}
						{#if hideTimePicker && i == 0}
							<Alert
								class="mb-m"
								type="info"
								isHeadingBold
								heading={$format('battery_life_inform', {
									default: 'Battery life information',
								})}
								message={$format('action_warning', {
									default:
										'Hooks in general, reduce battery life. Particularly, HTTPS hooks shortens battery life in half.',
								})}
							/>
						{/if}
						{#if peripherals && i == 0}
							<CardGrid gridGap="s" lg={290} sm={270} noPaddingBottom>
								{#each peripheralsOptions as option}
									<SelectCard
										cardBgColor={$showNewLayout ? 'color-1' : undefined}
										class="width-full"
										type="horizontal"
										variant="radio"
										title={option.title}
										selected={peripheralsIndex === option.value}
										on:click={() => onPeripheralChange(Number(option.value))}
									/>
								{/each}
							</CardGrid>
						{:else if components && i == 0}
							<CardGrid gridGap="s" lg={290} sm={270} noPaddingBottom>
								{#each componentsOptions as option}
									<SelectCard
										cardBgColor={$showNewLayout ? 'color-1' : undefined}
										class="width-full"
										type="horizontal"
										variant="radio"
										title={option.title}
										selected={componentIndex === option.value}
										on:click={() => onComponentsChange(Number(option.value))}
									/>
								{/each}
							</CardGrid>
						{:else if (chooseComponent && i == 1) || i == 0}
							<Input
								aria-labelledby="input-2uncx8h"
								size="m"
								type="text"
								icon="fas fa-link"
								tabIndex="-1"
								error={actionProps.name === '' && isInputDirty ? errors.name.label : ''}
								bind:value={actionProps.name}
								label={$format('label_name', {
									default: 'Name',
								})}
								on:blur={() => {
									isInputDirty = true;
								}}
								id="input-2uncx8h"
							/>
						{:else if isTimePickerStep}
							<Toggle
								size="l"
								bind:checked={actionProps.set_active_time}
								id="toggle-o3q954n"
							>
								{$format('modal_webhooks_set_active_time', {
									default: 'Set active time',
								})}
							</Toggle>
							{#if actionProps.set_active_time}
								<DoubleTimePicker
									bind:time={actionProps.active_between}
									class="mt-m"
									slot="bottom"
								/>
							{/if}
						{:else if (chooseComponent && i == 3) || i == (hideTimePicker ? 1 : 2)}
							<WebhookConditions
								{actionProps}
								bind:eventsOptions
								bind:eventsIndex
								error={errors.condition}
								on:change={changeEvent}
							/>
						{:else if (chooseComponent && i == 4) || i == (hideTimePicker ? 2 : 3)}
							<WebHooksActions
								{device}
								bind:urls={actionProps.urls}
								error={errors.urls}
								tokens={eventsOptions[eventsIndex]?.tokens}
							/>
						{/if}
					</Container>
				</WizardContent>
			{/each}
		</WizardContainer>
	</div>
	<div slot="footer" class="d-flex justify-content-end">
		{#if activeStep.activeIndex > 0}
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				icon="fas fa-chevron-left"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => {
					if (activeStep.activeIndex > 0) {
						activeStep = wizardItems[activeStep.activeIndex - 1];
					}
				}}
			>
				{$format('button_back', { default: 'Back' })}
			</Button>
		{/if}
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			icon={isLastStepWizard ? 'fas fa-save' : ''}
			rightIcon={isLastStepWizard ? '' : 'fas fa-chevron-right'}
			responsiveSize={{
				sm: 'm',
			}}
			loading={waitForResponse}
			disabled={(components != null && components.length == 0) || disabled}
			on:click={() => {
				if (isLastStepWizard) {
					save();
				} else {
					activeStep = wizardItems[activeStep.activeIndex + 1];
				}
			}}
		>
			{isLastStepWizard
				? $format('button_save', { default: 'Save' })
				: $format('button_next', { default: 'Next' })}
		</Button>
	</div>
</Modal>

<ExitConfirmationModal
	type={$format('the_action', { default: 'the action' })}
	{exitConfirmationModalState}
	{showExitConfirmationModal}
	showAddEditModal={showModal}
	on:close={(e) => {
		exitConfirmationModalState = e.detail.exitConfirmationModalState;
		showExitConfirmationModal = e.detail.showExitConfirmationModal;
	}}
	on:confirm={(e) => {
		showExitConfirmationModal = e.detail.showExitConfirmationModal;
		showWizard = e.detail.showAddEditModal;
		showModal = e.detail.showAddEditModal;
	}}
/>

<style>
	:global(.double-time-picker-disabled) {
		opacity: 0.5;
		pointer-events: none;
	}
</style>
